import { getTempLaw } from "../actions/temp_law";
import type { StoredLawDataProps, TempXMLLawDataProps, TempLawtextLawDataProps } from "./common";
import { storedLoader } from "./loaders";
import { searchLawID } from "./searchLawID";
import * as util from "lawtext/dist/src/util";
import type { LawDataResult, Timing } from "lawtext/dist/src/data/lawdata";
import { toLawData } from "lawtext/dist/src/data/lawdata";
import { lawNumLikeToLawNum, ptnLawNumLike } from "lawtext/dist/src/law/lawNum";
import parsePath from "lawtext/dist/src/path/v1/parse";
import { parseLawIDOrLawRevID } from "lawtext/dist/src/law/lawID";

// Regex pattern to detect XML format (XML declaration or Law element)
const XML_FORMAT_REGEX = /^(?:<\?xml|<Law)/;

export const navigateLawData = async (
    pathStr: string,
    onMessage: (message: string) => unknown,
    timing: Timing,
): Promise<{redirectPath: string} | LawDataResult<TempXMLLawDataProps | TempLawtextLawDataProps | StoredLawDataProps>> => {

    const firstPart = pathStr.split("/", 1)[0];

    const text = getTempLaw(firstPart);
    if (text !== null) {
        if (XML_FORMAT_REGEX.test(text.trim())) {
            onMessage("法令XMLをパースしています...");
            // console.log("navigateLawData: parsing law xml...");
            await util.wait(30);
            return toLawData({
                source: "temp_xml",
                xml: text,
                lawXMLStruct: null,
            }, onMessage, timing);
        } else {
            onMessage("Lawtextをパースしています...");
            // console.log("navigateLawData: parsing lawtext...");
            await util.wait(30);
            return toLawData({
                source: "temp_lawtext",
                lawtext: text,
            }, onMessage, timing);
        }
    }

    let lawIDOrLawNum: string | null;

    {
        let lawID: string | null = null;
        let lawnum: string | null = null;

        const v1Match = /^v1:(.+)$/.exec(pathStr);
        if (v1Match) {
            const path = parsePath(v1Match[1]);
            if (!path.ok) {
                return {
                    ok: false,
                    error: new Error(`パス「${v1Match[1]}」にエラーがあります：${JSON.stringify(path.errors)}`),
                };
            }
            const firstFragment = path.value[0];
            if (firstFragment.type !== "LAW") {
                return {
                    ok: false,
                    error: new Error(`パス「${v1Match[1]}」の先頭には法令IDを指定してください。`),
                };
            }
            lawID = firstFragment.text;
        }

        const reLawNumLike = new RegExp(`^(?:${ptnLawNumLike})$`);
        if (lawID === null && parseLawIDOrLawRevID(firstPart)) {
            lawID = firstPart;

        } else if (reLawNumLike.test(firstPart)) {
            lawnum = lawNumLikeToLawNum(firstPart);

        }

        lawIDOrLawNum = lawID ?? lawnum;

    }


    if (lawIDOrLawNum === null) {
        onMessage("法令IDを検索しています...");
        // console.log("navigateLawData: searching lawid...");
        const [searchLawIDTime, lawIDResult] = await util.withTime(searchLawID)(firstPart);
        timing.searchLawID = searchLawIDTime;

        if (!lawIDResult) {
            return {
                ok: false,
                error: new Error(`「${firstPart}」を検索しましたが、見つかりませんでした。`),
            };
        } else if (typeof lawIDResult !== "string") {
            return {
                ok: false,
                error: new Error(`「${firstPart}」の検索時にエラーが発生しました： ${lawIDResult.error}: "${lawIDResult.message}"`),
            };
        }

        // For local files that don't have real law IDs (e.g., using base64 IDs),
        // try to parse as law ID first. If it fails, use the ID directly without v1: prefix.
        if (parseLawIDOrLawRevID(lawIDResult)) {
            return {
                redirectPath: [`v1:${lawIDResult}`, ...pathStr.split("/").slice(1)].join("/"),
            };
        } else {
            // Use the ID directly for local files
            lawIDOrLawNum = lawIDResult;
        }
    }


    try {
        onMessage("保存されている法令情報を探しています...");
        // console.log(`navigateLawData: fetching stored law info for "${lawnum}"...`);
        const [fetchStoredLawInfoTime, lawInfo] = await util.withTime(storedLoader.getLawInfoByLawIDOrLawNum.bind(storedLoader))(lawIDOrLawNum);
        timing.fetchStoredLawInfo = fetchStoredLawInfoTime;
        if (!lawInfo) throw null;

        onMessage("保存されている法令XMLの取得を試みています...");
        // console.log("navigateLawData: fetching stored law data...");
        const [loadDataTime, lawXMLStruct] = lawInfo && await util.withTime(storedLoader.loadLawXMLStructByInfo.bind(storedLoader))(lawInfo);
        timing.loadData = loadDataTime;

        // Check if the file is Lawtext format (not XML)
        const isLawtextFile = lawInfo.XmlName.endsWith(".law.txt");
        const text = lawXMLStruct.xml;
        
        if (isLawtextFile || !XML_FORMAT_REGEX.test(text.trim())) {
            onMessage("Lawtextをパースしています...");
            // console.log("navigateLawData: parsing lawtext...");
            await util.wait(30);
            return toLawData({
                source: "temp_lawtext",
                lawtext: text,
            }, onMessage, timing);
        } else {
            onMessage("法令XMLをパースしています...");
            // console.log("navigateLawData: parsing law xml...");
            await util.wait(30);
            return toLawData({
                source: "stored",
                xml: text,
                lawPath: lawInfo.Path,
                lawXMLStruct,
            }, onMessage, timing);
        }
    } catch {
        //
    }

    return {
        ok: false,
        error: new Error(`「${lawIDOrLawNum}」の法令データが見つかりませんでした。ローカルに保存された法令データのみ参照可能です。`),
    };
};
